import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { MAX_TIMEOUT_DELAY_MS } from '../constants/timers.js';
import { resolveActiveSuppressionEndMs } from '../lib/active-suppression-end.js';
import {
  EMPTY_ACTIVE_SUPPRESSIONS,
  useActiveSuppressionsStore,
} from '../store/activeSuppressions.js';
import type { ActiveSuppressionRecord } from '../types';

export const useLiveActiveSuppressions = (deviceId: string): ActiveSuppressionRecord[] => {
  const records = useActiveSuppressionsStore(
    (state) => state.byDevice[deviceId] ?? EMPTY_ACTIVE_SUPPRESSIONS,
  );
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Before paint: nowMs can be far behind a store update, and a record that arrives already ended
  // would otherwise be painted until the zero-delay timer fires.
  useLayoutEffect(() => {
    setNowMs(Date.now());
  }, [records]);

  const liveRecords = useMemo(
    () => records.filter((record) => resolveActiveSuppressionEndMs(record) > nowMs),
    [records, nowMs],
  );

  // A number, so the effect is stable across the fresh array identity every filter produces.
  const nextEndMs = liveRecords.length
    ? Math.min(...liveRecords.map(resolveActiveSuppressionEndMs))
    : null;

  // nowMs re-arms the timer when a capped delay fires short of the end.
  useEffect(() => {
    if (nextEndMs === null) {
      return;
    }

    const delayMs = Math.min(Math.max(0, nextEndMs - Date.now()), MAX_TIMEOUT_DELAY_MS);
    const endTimer = setTimeout(() => setNowMs(Date.now()), delayMs);

    return () => clearTimeout(endTimer);
  }, [nextEndMs, nowMs]);

  return liveRecords;
};
